import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getUser,
  putUser,
  postNewPrefessionals,
  postUserPassword,
  postPassword,
  putVerifyEmail,
  getUserAccess,
  putUserAccess,
} from "services/api";
import {
  normalizeDataProfessional
} from './operations';


export interface ProfessionalDetailState {
  professionalData: {
    data: null | any,
    status: null | 'idle' | 'loading' | 'failed',
  }
  newProfessional: any
  updateProfessional: any,
  changePassword: any,
  changePasswordProfessional: any,
  /** Acceso a la plataforma del profesional: estado, motivo y autoría del último cambio. */
  access: {
    data: null | any,
    status: null | 'idle' | 'loading' | 'failed',
  },
  /** Resultado del último bloqueo/reactivación, para el aviso de la pantalla. */
  updateAccess: null | { success: boolean, errorCode?: number },
};

const initialState: ProfessionalDetailState = {
  professionalData: {
    data: null,
    status: null,
  },
  newProfessional: null,
  updateProfessional: null,
  changePassword: null,
  changePasswordProfessional: null,
  access: {
    data: null,
    status: null,
  },
  updateAccess: null,
};

export const getUserAsync: any | null = createAsyncThunk(
  'professionalDetail/getUser',
  async (data: any) => {
    const response = await getUser(data);
    return response?.data;
  }
);

export const putUserAsync: any | null = createAsyncThunk(
  'professionalDetail/putUser',
  async (data: any) => {
    const response = await putUser(data);
    return response;
  }
);

export const postNewPrefessionalsAsync: any | null = createAsyncThunk(
  'professionalDetail/postNewPrefessionals',
  async (data: any) => {
    const response = await postNewPrefessionals(data);
    return response;
  }
);

export const postUserPasswordAsync: any | null = createAsyncThunk(
  'professionalDetail/postUserPassword',
  async (data: any) => {
    const response = await postUserPassword(data.id, data);
    return response;
  }
);

export const postPasswordAsync: any | null = createAsyncThunk(
  'professionalDetail/postPassword',
  async (data: any) => {
    const response = await postPassword(data);
    return response;
  }
);

export const putVerifyEmailAsync: any | null = createAsyncThunk(
  'professionalDetail/putVerifyEmail',
  async (data: any) => {
    const response = await putVerifyEmail({ email: data });
    return response;
  }
);


export const getUserAccessAsync: any | null = createAsyncThunk(
  'professionalDetail/getUserAccess',
  async (id: string) => {
    const response = await getUserAccess(id);
    return response?.data;
  }
);

export const putUserAccessAsync: any | null = createAsyncThunk(
  'professionalDetail/putUserAccess',
  async (data: { id: string, status: string, reasonCode?: string | null, reasonText?: string | null }) => {
    const { id, ...body } = data;
    const response = await putUserAccess(id, body);
    return response?.data;
  }
);

export const professionalDetailSlice = createSlice({
  name: 'professionalDetail',
  initialState,
  reducers: {
    resetProfessional: (state) => {
      state.professionalData = {
        data: null,
        status: null,
      };
      state.newProfessional = null;
      state.changePassword = null;
      state.access = { data: null, status: null };
      state.updateAccess = null;
    },
    resetNewProfessional: (state) => {
      state.newProfessional = null;
    },
    resetUpdateProfessional: (state) => {
      state.updateProfessional = null;
    },
    resetUpdateAccess: (state) => {
      state.updateAccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserAsync.pending, (state) => {
        state.professionalData.status = 'loading';
      })
      .addCase(getUserAsync.fulfilled, (state, action) => {
        state.professionalData.status = 'idle';
        state.professionalData.data = normalizeDataProfessional(action.payload);
      })
      .addCase(getUserAsync.rejected, (state) => {
        state.professionalData.status = 'failed';
      })
      .addCase(putUserAsync.fulfilled, (state, action) => {
        state.professionalData.status = 'idle';
        state.professionalData.data = action.meta.arg;
        state.updateProfessional =  action.payload?.data?.success;
      })
      .addCase(postNewPrefessionalsAsync.fulfilled, (state, action) => {
        state.newProfessional =  action.payload?.data?.success;
      })
      .addCase(postUserPasswordAsync.fulfilled, (state, action) => {
        state.changePassword = action.payload?.data;
      })
      .addCase(postPasswordAsync.fulfilled, (state, action) => {
        state.changePasswordProfessional = action.payload?.data;
      })
      .addCase(getUserAccessAsync.pending, (state) => {
        state.access.status = 'loading';
      })
      .addCase(getUserAccessAsync.fulfilled, (state, action) => {
        state.access.status = 'idle';
        state.access.data = action.payload?.item ?? null;
      })
      .addCase(getUserAccessAsync.rejected, (state) => {
        state.access.status = 'failed';
      })
      .addCase(putUserAccessAsync.fulfilled, (state, action) => {
        state.updateAccess = {
          success: !!action.payload?.success,
          errorCode: action.payload?.errorCode,
        };
      })
      .addCase(putUserAccessAsync.rejected, (state) => {
        state.updateAccess = { success: false };
      })
  }
});

export const {
  resetProfessional,
  resetNewProfessional,
  resetUpdateProfessional,
  resetUpdateAccess,
} = professionalDetailSlice.actions;

export default professionalDetailSlice.reducer;
